import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { DateRangeFilter } from 'components/DateRangeFilter';
import { getColumnId, getColumnsWithPinnedProp, onTableSettingsConfirm } from 'components/TableSettings';
import { UerChapter, UerClassifier, WorkInSumTitles } from 'constantsProject';
import { SortEnumType, type CatalogUerBaseCostData, type ChapterEnum, type WorkInSumEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { usePin } from 'hooks/usePin';
import { useSelection } from 'hooks/useSelection';
import { Button, IconExpand, Loader } from 'uikit';
import { formatDateToLocale, formatNumberToRu } from 'utils';

import type { ColDef, ColGroupDef } from 'ag-grid-community';
import type React from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useMemo } from 'react';

import { CollapseHeaderButton } from '../../Common/EstimateBasisModal/components/CollapseHeaderButton';
import {
    catalogUERBaseCostChapterFilter,
    catalogUERBaseCostCodeFilter,
    catalogUERBaseCostGroupNameFilter,
    catalogUERBaseCostNameFilter,
    catalogUERBaseCostSectionNameFilter,
    catalogUERBaseCostUerClassifierFilter,
    catalogUERBaseCostWorktypeFOsummFilter,
    catalogUERBaseCostWorktypeNameFilter,
} from '../catalogUerBaseCostQueries';
import { CheckRowCell } from '../components/CheckRowCell';
import { RowMenu } from '../RowMenu';

const collapseKeys = ['ozp', 'emm', 'zpm', 'mtr', 'eqp', 'trn', 'pir', 'nr', 'sp'];

const catalogCascadeInputFields = {
    sectionName: 'sectionNames',
    groupName: 'groupNames',
    code: 'codes',
    name: 'names',
    workName: 'worktypeNames',
    chapter: 'chapters',
    uerClassifier: 'uerClassifiers',
    workInSum: 'workInSums',
} as const;

const getCatalogFilterInput = (filterValues: Record<string, string[]> | undefined, excludeField: string) =>
    Object.entries(catalogCascadeInputFields).reduce<Record<string, string[]>>((input, [field, key]) => {
        if (field === excludeField) {
            return input;
        }

        const values = filterValues?.[field]?.filter((value) => typeof value === 'string');

        if (values?.length) {
            input[key] = values;
        }

        return input;
    }, {});

interface ICatalogBaseCostColumns {
    handleSetCheckedIds: Dispatch<SetStateAction<{ [key: number]: boolean }>>;
    checkedIds: { [key: number]: boolean };
    handleExpandRow: (data: CatalogUerBaseCostData) => void;
    catalogUniqueId?: string;
    isUERBaseCostWork: boolean;
    handleOpenModalEstimate: () => void;
    setRowData: Dispatch<SetStateAction<CatalogUerBaseCostData | null>>;
    onRemoveSuccess: () => void;
    filterValues: Record<string, string[]>;
    setFilterValues: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
    catalogPriceRules: IUseAccess['catalogPrice'];
    onEstimateBasisModalOpen: () => void;
}
export const numberFormatter = ({ value }: { value: number }) => formatNumberToRu(value, 2);
export const useCatalogBaseCostColumns = ({
    checkedIds,
    handleSetCheckedIds,
    handleExpandRow,
    catalogUniqueId,
    isUERBaseCostWork,
    handleOpenModalEstimate,
    setRowData,
    onRemoveSuccess,
    filterValues,
    setFilterValues,
    catalogPriceRules,
    onEstimateBasisModalOpen,
}: ICatalogBaseCostColumns) => {
    const handleChangeCheckbox = useCallback(
        (rowId: number) =>
            ({ checked }: { checked: boolean }) => {
                handleSetCheckedIds((ids: any) => ({ ...ids, [rowId]: checked }));
            },
        [handleSetCheckedIds],
    );

    const changeFilterHandler = useCallback<(params: { fieldName: string; checkedValues: string[] }) => void>(
        ({ fieldName, checkedValues }) => {
            setFilterValues((prevValue) => ({
                ...prevValue,
                [fieldName]: checkedValues,
            }));
        },
        [setFilterValues],
    );

    const variables = useMemo(() => ({ catalogUniqueId, order: { value: SortEnumType.Asc } }), [catalogUniqueId]);

    const columns = useMemo<(ColDef<CatalogUerBaseCostData> | ColGroupDef<CatalogUerBaseCostData>)[]>(
        () => [
            {
                headerName: '',
                field: 'menu' as any,
                maxWidth: 106,
                minWidth: 106,
                spanHeaderHeight: true,
                pinned: 'left',
                lockPosition: 'left',
                cellClass: 'centeredAgGridCell',
                cellRenderer: ({ data }: { data?: CatalogUerBaseCostData }) =>
                    !data ? (
                        <Loader />
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <CheckRowCell
                                disabled={!catalogPriceRules.canDelete}
                                row={data}
                                checkedIds={checkedIds}
                                onChange={handleChangeCheckbox(data.id)}
                            />
                            <RowMenu
                                setRowData={setRowData}
                                row={data}
                                handleOpenModalEstimate={handleOpenModalEstimate}
                                onRemoveSuccess={onRemoveSuccess}
                                onEstimateBasisModalOpen={onEstimateBasisModalOpen}
                                canRead={catalogPriceRules.canRead}
                                canEdit={catalogPriceRules.canEdit}
                                canDelete={catalogPriceRules.canDelete}
                            />
                            <Button
                                onClick={() => handleExpandRow(data)}
                                iconLeft={IconExpand}
                                onlyIcon
                                view="clear"
                                size="xs"
                            />
                        </div>
                    ),
            },
            {
                headerName: 'Дата обновления',
                field: 'updatedAt',
                width: 150,
                spanHeaderHeight: true,
                valueFormatter: ({ value }) => formatDateToLocale(value),
                filter: DateRangeFilter,
                filterParams: {
                    changeFilterHandler,
                },
            },
            {
                headerName: 'Раздел',
                field: 'sectionName',
                width: 200,
                spanHeaderHeight: true,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    changeFilterHandler,
                    dataAccessor: 'catalogUERBaseCostSectionNameFilter',
                    whereVariables: ['value'],
                    variables: {
                        ...variables,
                        input: getCatalogFilterInput(filterValues, 'sectionName'),
                    },
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Раздел',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: catalogUERBaseCostSectionNameFilter,
                },
            },
            {
                headerName: 'Подраздел',
                field: 'groupName',
                width: 200,
                spanHeaderHeight: true,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    changeFilterHandler,
                    dataAccessor: 'catalogUERBaseCostGroupNameFilter',
                    whereVariables: ['value'],
                    variables: {
                        ...variables,
                        input: getCatalogFilterInput(filterValues, 'groupName'),
                    },
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Подраздел',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: catalogUERBaseCostGroupNameFilter,
                },
            },
            {
                headerName: 'Шифр',
                field: 'code',
                width: 200,
                spanHeaderHeight: true,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    changeFilterHandler,
                    dataAccessor: 'catalogUERBaseCostCodeFilter',
                    whereVariables: ['value'],
                    variables: {
                        ...variables,
                        input: getCatalogFilterInput(filterValues, 'code'),
                    },
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Шифр',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: catalogUERBaseCostCodeFilter,
                },
            },
            {
                headerName: 'Наименование',
                field: 'name',
                width: 400,
                spanHeaderHeight: true,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    changeFilterHandler,
                    dataAccessor: 'catalogUERBaseCostNameFilter',
                    whereVariables: ['value'],
                    variables: {
                        ...variables,
                        input: getCatalogFilterInput(filterValues, 'name'),
                    },
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Наименование',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: catalogUERBaseCostNameFilter,
                },
            },
            {
                headerName: 'Ед.изм.',
                field: 'unit',
                width: 88,
                spanHeaderHeight: true,
            },
            {
                headerName: 'Стоимость единицы в базисном уровне цен',
                spanHeaderHeight: true,
                width: 120,
                children: [
                    {
                        headerName: '',
                        field: 'unitCost',
                        cellClass: 'ag-right-aligned-cell',
                        spanHeaderHeight: true,
                        headerComponent: CollapseHeaderButton,
                        headerComponentParams: {
                            collapseKeys,
                        },
                        valueFormatter: numberFormatter,
                    },
                    {
                        headerName: 'ОЗП',
                        field: 'ozp',
                        width: 152,
                        cellClass: 'ag-right-aligned-cell',
                        valueFormatter: numberFormatter,
                    },
                    {
                        headerName: 'ЭММ',
                        field: 'emm',
                        width: 152,
                        cellClass: 'ag-right-aligned-cell',
                        valueFormatter: numberFormatter,
                    },
                    {
                        headerName: 'ЗПМ',
                        field: 'zpm',
                        width: 152,
                        cellClass: 'ag-right-aligned-cell',
                        valueFormatter: numberFormatter,
                    },
                    {
                        headerName: 'МТ',
                        field: 'mtr',
                        width: 152,
                        cellClass: 'ag-right-aligned-cell',
                        valueFormatter: numberFormatter,
                    },
                    {
                        headerName: 'Оборудование',
                        field: 'eqp',
                        width: 152,
                        cellClass: 'ag-right-aligned-cell',
                        valueFormatter: numberFormatter,
                    },
                    {
                        headerName: 'Перевозка',
                        field: 'trn',
                        width: 152,
                        cellClass: 'ag-right-aligned-cell',
                        valueFormatter: numberFormatter,
                    },
                    {
                        headerName: 'ПИР',
                        field: 'pir',
                        width: 152,
                        cellClass: 'ag-right-aligned-cell',
                        valueFormatter: numberFormatter,
                    },
                    {
                        headerName: 'НР',
                        field: 'nr',
                        width: 152,
                        cellClass: 'ag-right-aligned-cell',
                        valueFormatter: numberFormatter,
                    },
                    {
                        headerName: 'СП',
                        field: 'sp',
                        width: 152,
                        cellClass: 'ag-right-aligned-cell',
                        valueFormatter: numberFormatter,
                    },
                ],
            },
            {
                headerName: 'Глава в расчете',
                field: 'chapter',
                width: 125,
                spanHeaderHeight: true,
                valueFormatter: ({ value }) => UerChapter[value] ?? '',
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    changeFilterHandler,
                    dataAccessor: 'catalogUERBaseCostChapterFilter',
                    whereVariables: ['value'],
                    variables: {
                        ...variables,
                        input: getCatalogFilterInput(filterValues, 'chapter'),
                    },
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Глава в расчете',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                            valueFormatter: ({ value }: { value: ChapterEnum | null }) =>
                                value ? UerChapter[value] : '',
                        },
                    ],
                    query: catalogUERBaseCostChapterFilter,
                },
            },
            {
                headerName: 'Тип расценки',
                field: 'uerClassifier',
                width: 152,
                spanHeaderHeight: true,
                cellRenderer: ({ value }: { value: string }) => UerClassifier[value] || '',
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    changeFilterHandler,
                    dataAccessor: 'catalogUERBaseCostUerClassifierFilter',
                    whereVariables: ['value'],
                    variables: {
                        ...variables,
                        input: getCatalogFilterInput(filterValues, 'uerClassifier'),
                    },
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Тип расценки',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                            valueFormatter: ({ value }: { value: string }) => UerClassifier[value] || '',
                        },
                    ],
                    query: catalogUERBaseCostUerClassifierFilter,
                },
            },
            {
                headerName: 'Вид строительства',
                field: 'constructionTypes',
                width: 200,
                spanHeaderHeight: true,
                valueGetter: ({ data }: { data?: CatalogUerBaseCostData }) =>
                    data?.constructionTypes?.map((item: { name: string }) => item.name).join('; ') ?? '',
                cellRenderer: ({ value }: { value: string }) => <div className="trunCate">{value}</div>,
            },
            ...(isUERBaseCostWork
                ? []
                : [
                      {
                          headerName: 'Виды работ',
                          field: 'workTypes' as any,
                          children: [
                              {
                                  headerName: 'Шифр',
                                  field: 'workType.workCode' as any,
                                  spanHeaderHeight: true,
                                  width: 136,
                              },
                              {
                                  headerName: 'Наименование',
                                  field: 'workName',
                                  spanHeaderHeight: true,
                                  width: 164,
                                  valueFormatter: ({ data }: { data?: CatalogUerBaseCostData }) =>
                                      data?.workType?.workName,
                                  filter: AgGridCustomFilterAdapter,
                                  filterParams: {
                                      hideTableHeader: true,
                                      changeFilterHandler,
                                      dataAccessor: 'catalogUERBaseCostWorktypeNameFilter',
                                      whereVariables: ['value'],
                                      variables: {
                                          ...variables,
                                          input: getCatalogFilterInput(filterValues, 'workName'),
                                      },
                                      childFieldAccessor: 'value',
                                      columns: [
                                          {
                                              headerName: 'Наименование',
                                              field: 'value',
                                              checkboxSelection: true,
                                              width: 400,
                                          },
                                      ],
                                      query: catalogUERBaseCostWorktypeNameFilter,
                                  },
                              },
                              {
                                  headerName: 'Ед.изм.',
                                  field: 'workType.workUnit',
                                  spanHeaderHeight: true,
                                  width: 88,
                              },
                              {
                                  headerName: 'Участие в суммировании ФО по виду работ',
                                  field: 'workInSum',
                                  spanHeaderHeight: true,
                                  wrapText: false,
                                  width: 162,
                                  headerClass: 'nowrapWork',
                                  valueFormatter: ({ data }: { data?: CatalogUerBaseCostData }) =>
                                      data?.workType?.workInSum ? WorkInSumTitles[data?.workType?.workInSum] : '',
                                  filter: AgGridCustomFilterAdapter,
                                  filterParams: {
                                      hideTableHeader: true,
                                      changeFilterHandler,
                                      dataAccessor: 'catalogUERBaseCostWorktypeFOsummFilter',
                                      whereVariables: ['value'],
                                      variables: {
                                          ...variables,
                                          input: getCatalogFilterInput(filterValues, 'workInSum'),
                                      },
                                      childFieldAccessor: 'value',
                                      columns: [
                                          {
                                              headerName: 'Участие в суммировании ФО по виду работ',
                                              field: 'value',
                                              checkboxSelection: true,
                                              width: 400,
                                              valueFormatter: ({ value }: { value: WorkInSumEnum | null }) =>
                                                  value ? WorkInSumTitles[value] : '',
                                          },
                                      ],
                                      query: catalogUERBaseCostWorktypeFOsummFilter,
                                  },
                              },
                              {
                                  headerName: 'К перехода',
                                  field: 'workType.workRatio',
                                  valueFormatter: ({ value }: { value: number }) => formatNumberToRu(value, 4),
                                  spanHeaderHeight: true,
                                  width: 136,
                              },
                          ],
                      },
                  ]),
            {
                headerName: 'Обоснование',
                field: 'justification',
                width: 200,
                spanHeaderHeight: true,
            },
        ],
        [
            catalogPriceRules.canDelete,
            catalogPriceRules.canEdit,
            catalogPriceRules.canRead,
            changeFilterHandler,
            filterValues,
            checkedIds,
            handleChangeCheckbox,
            handleExpandRow,
            handleOpenModalEstimate,
            isUERBaseCostWork,
            onEstimateBasisModalOpen,
            onRemoveSuccess,
            setRowData,
            variables,
        ],
    );

    const [selection, , , setSelection] = useSelection('catalogBaseCostTable');
    const [pinnedItems, , , setPinned] = usePin('catalogBaseCostTable');

    const isColumnVisible = useCallback((id: string) => selection[id] ?? true, [selection]);

    const fullSetColumns = useMemo(() => {
        const fullSetColumnsWithPins = getColumnsWithPinnedProp({ columns, pinnedItems });

        return fullSetColumnsWithPins.filter((column) => isColumnVisible(getColumnId(column)));
    }, [columns, isColumnVisible, pinnedItems]);

    const fullSetSettingsColumns = useMemo(
        () =>
            columns.reduce((acc: ColDef[], column, idx) => {
                if (idx > 0) {
                    const newColumn = { ...column } as ColDef & { disabled: boolean };

                    if (idx < 6) {
                        newColumn.disabled = true;
                    }

                    if (pinnedItems[getColumnId(column)]) {
                        newColumn.pinned = true;
                    }

                    acc.push(newColumn);
                }

                return acc;
            }, []),
        [columns, pinnedItems],
    ) as any[];

    const handleTableSettingsConfirm = useCallback(
        (visible) => {
            onTableSettingsConfirm({
                settingColumns: fullSetSettingsColumns,
                incomingColumns: visible,
                setPinned,
                setSelection,
            });
        },
        [fullSetSettingsColumns, setPinned, setSelection],
    );

    return { fullSetColumns, fullSetSettingsColumns, isColumnVisible, handleTableSettingsConfirm };
};
